/**
 * Clinical AI Agent - Teaching Tab
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GraduationCap, Loader2, CheckCircle } from "lucide-react";
import { TEACHING_TOPICS, AUDIENCE_OPTIONS } from "./constants";

export function TeachingForm({
  teachingForm,
  setTeachingForm,
  loading,
  isDark,
  onSubmit
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Topic</Label>
        <Select
          value={teachingForm.topic}
          onValueChange={(v) => setTeachingForm({ ...teachingForm, topic: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEACHING_TOPICS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Audience</Label>
        <Select
          value={teachingForm.audience}
          onValueChange={(v) => setTeachingForm({ ...teachingForm, audience: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUDIENCE_OPTIONS.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Additional Context</Label>
        <Textarea
          placeholder="Any specific concerns or focus areas..."
          value={teachingForm.context}
          onChange={(e) => setTeachingForm({ ...teachingForm, context: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={3}
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
        data-testid="generate-teaching-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GraduationCap className="h-4 w-4 mr-2" />}
        Generate Teaching Content
      </Button>
    </div>
  );
}

export function TeachingResultDisplay({ result, isDark }) {
  if (!result || result.type !== "teaching") return null;

  const { data } = result;

  return (
    <Card className={`mt-4 ${isDark ? "bg-slate-800/80 border-purple-500/30" : "bg-purple-50 border-purple-200"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          {data.title || "Teaching Content Generated"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-80">
          {data.key_points && data.key_points.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold mb-2">Key Points:</p>
              <ul className="space-y-2">
                {data.key_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Badge variant="outline" className="text-xs shrink-0">{i + 1}</Badge>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.detailed_content && (
            <div className="mb-4">
              <p className="text-xs font-semibold mb-1">Detailed Content:</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                {data.detailed_content}
              </p>
            </div>
          )}

          {data.action_items && data.action_items.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold mb-1">Action Items:</p>
              <ul className="text-xs space-y-1">
                {data.action_items.map((item, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-purple-400">→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.warning_signs && data.warning_signs.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold mb-1 text-red-400">When to Call Hospice:</p>
              <ul className="text-xs space-y-1">
                {data.warning_signs.map((sign, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-red-400">⚠</span>
                    {sign}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.teach_back_questions && data.teach_back_questions.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold mb-1">Teach-Back Questions:</p>
              <ul className="text-xs space-y-1">
                {data.teach_back_questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-cyan-400">?</span>
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.documentation_note && (
            <div className="p-2 rounded bg-gray-100 dark:bg-slate-700">
              <p className="text-xs font-semibold mb-1">Documentation Note:</p>
              <p className="text-xs italic">{data.documentation_note}</p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
